import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';
import PaginatedFilter from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { formatBundleDate } from '@app/utils';

import { useBundleDates } from '../../hooks';

const messages = defineMessages({
  bundleDate: {
    id: 'appUpdates.bundles.filters.bundleDate.label',
    description: 'Label for the bundle date filter',
    defaultMessage: 'Preview release date',
  },
});

export const BundleDateFilter: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();

  const selectedBundleDate = state.bundles.draftFilters.selectedBundleDate;
  const selectedProducts = state.bundles.draftFilters.selectedProducts;

  const { data: bundleDates = [], isLoading } =
    useBundleDates(selectedProducts);

  const getSelectedValue = (): SingleOption[] =>
    selectedBundleDate
      ? [
          {
            label: formatBundleDate(selectedBundleDate),
            value: selectedBundleDate,
            optionType: 'option',
          },
        ]
      : [];

  // PaginatedFilter is inherently multi-select; enforce single-select by keeping
  // only the most recently chosen option.
  const onBundleDateChange = (options: readonly SingleOption[]) => {
    const latest = options.length > 0 ? options[options.length - 1] : null;

    dispatch({
      type: 'BUNDLE_FILTER_DATE_SELECT',
      payload: { selectedBundleDate: latest ? String(latest.value) : null },
    });
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = bundleDates.map((date) => ({
      label: formatBundleDate(date),
      value: date,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({ options, totalCount: options.length });
  };

  return (
    <PaginatedFilter
      testId="app-updates-bundle-filter-date-testid"
      fieldId="app-updates-bundle-date-filter"
      fieldLabel={formatMessage(messages.bundleDate)}
      onLoadOptions={onLoadOptions}
      value={getSelectedValue()}
      onChange={onBundleDateChange}
      onClearValues={() => onBundleDateChange([])}
      isLoading={isLoading}
    />
  );
};
